import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";

declare global {
  interface Window {
    google?: {
      charts: {
        load: (version: string, options: { packages: string[] }) => void;
        setOnLoadCallback: (cb: () => void) => void;
      };
      visualization: {
        arrayToDataTable: (data: (string | number)[][]) => any;
        ComboChart: new (el: HTMLElement) => {
          draw: (data: any, options: any) => void;
        };
      };
    };
  }
}

const getChartTitle = (dataField: string): string => {
  switch (dataField) {
    case "hourly_distribution_content_uploaded":
      return "Content Uploaded";
    case "hourly_distribution_by_denmark":
      return "Platform Published";
    case "hourly_distribution_by_social":
      return "Social Published";
    default:
      return "Data";
  }
};

const getChartData = (
  todayData: any,
  yesterdayData: any,
  dataField: string
): (string | number)[][] => {
  const data: (string | number)[][] = [["Time", "Today", "Yesterday"]];
  const hourlyData = new Map<
    number,
    { today: number; yesterday: number; timeLabel: string }
  >();

  // Process today's data
  const todayHourlyData = todayData?.[dataField];
  if (todayHourlyData && Array.isArray(todayHourlyData)) {
    todayHourlyData.forEach((item: any) => {
      const hour = item.hour ?? new Date(item.key).getHours();
      const existing = hourlyData.get(hour) || {
        today: 0,
        yesterday: 0,
        timeLabel: 0,
      };
      hourlyData.set(hour, {
        ...existing,
        today: item.doc_count || 0,
        timeLabel: item.time_label,
      });
    });
  }

  // Process yesterday's data
  const yesterdayHourlyData = yesterdayData?.[dataField];
  if (yesterdayHourlyData && Array.isArray(yesterdayHourlyData)) {
    yesterdayHourlyData.forEach((item: any) => {
      const hour = item.hour ?? new Date(item.key).getHours();
      const existing = hourlyData.get(hour) || {
        today: 0,
        yesterday: 0,
        timeLabel: 0,
      };
      hourlyData.set(hour, {
        ...existing,
        yesterday: item.doc_count || 0,
        timeLabel: item.time_label,
      });
    });
  }
  const sortedData = Array.from(hourlyData.entries())
    .sort(([a], [b]) => a - b)
    .map(([_, data]) => [data.timeLabel, data.today, data.yesterday]);

  data.push(...sortedData);
  return data;
};

const getChartOptions = (dataField: string, maxValue?: number) => ({
  vAxis: {
    gridlines: {
      count: 0,
    },
    minValue: 0,
    maxValue: maxValue || 50,
    baselineColor: "#ccc",
    viewWindow: {
      min: 0,
      max: maxValue || 50,
    },
  },
  hAxis: { showTextEvery: 3 },
  chartArea: {
    width: "100%",
    height: "70%",
    top: "15%",
    left: "5%",
    right: "5%",
    bottom: "20%",
  },
  colors: ["#1a76d2", "#e3f2fd"],
  legend: { position: "top" },
  backgroundColor: "transparent",
  series: {
    0: {
      type: "bars",
      color: "#1a76d2",
      targetAxisIndex: 0,
    },
    1: {
      type: "area",
      color: "#a9acb1",
      opacity: 0.5,
      targetAxisIndex: 0,
      lineWidth: 0,
      areaOpacity: 0.5,
    },
  },
  isStacked: false,
});

interface HomeListData {
  media_today_agg?: any;
  media_yesterday_agg?: any;
}

const AnalyticsChart: React.FC<{
  updateModal: any;
  CHART_SCRIPT_URL: string;
  homeList: HomeListData;
  dataField: string;
}> = ({ updateModal, CHART_SCRIPT_URL, homeList, dataField }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const drawChart = () => {
    if (!window.google?.visualization || !chartRef.current) return;

    setIsLoading(true);
    const containerWidth =
      chartRef.current.parentElement?.offsetWidth ||
      chartRef.current.offsetWidth;
    const todayData = homeList?.media_today_agg || {};
    const yesterdayData = homeList?.media_yesterday_agg || {};
    const chartData = getChartData(todayData, yesterdayData, dataField);

    if (chartData.length > 1) {
      const maxValue = Math.max(
        10,
        ...chartData
          .slice(1)
          .map((row) => Math.max(row[1] as number, row[2] as number))
      );
      const dataTable = window.google.visualization.arrayToDataTable(chartData);
      const chartOptions = {
        ...getChartOptions(dataField, maxValue),
        width: containerWidth,
        height: 200,
      };

      const chart = new window.google.visualization.ComboChart(
        chartRef.current
      );
      chart.draw(dataTable, chartOptions);
    }

    setTimeout(() => setIsLoading(false), 300);
  };

  const loadGoogleCharts = () => {
    if (!window.google) return;
    window.google.charts.load("current", { packages: ["corechart"] });
    window.google.charts.setOnLoadCallback(drawChart);
  };

  useEffect(() => {
    if (!window.google?.charts) {
      const script = document.createElement("script");
      script.src = CHART_SCRIPT_URL;
      script.async = true;
      script.onload = loadGoogleCharts;
      document.body.appendChild(script);
    } else {
      loadGoogleCharts();
    }

    const handleResize = () => setTimeout(() => drawChart(), 50);
    window.addEventListener("resize", handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if (chartContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() =>
        setTimeout(() => drawChart(), 50)
      );
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
    };
  }, [homeList?.media_today_agg, homeList?.media_yesterday_agg, dataField]);

  useEffect(() => {
    if (window.google?.visualization) {
      setIsLoading(true);
      setTimeout(() => drawChart(), 100);
    }
  }, [homeList?.media_today_agg, homeList?.media_yesterday_agg, dataField]);

  return (
    <>
      <div
        ref={chartContainerRef}
        className="position-relative w-100"
        style={{ height: "200px" }}
      >
        {isLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
            style={{ zIndex: 10 }}
          >
            <div className="d-flex flex-column align-items-center gap-2">
              <Spinner animation="border" variant="primary" />
              <span className="text-muted small">Loading chart...</span>
            </div>
          </div>
        )}
        <div ref={chartRef} className="w-100" style={{ height: "200px" }} />
      </div>

      <div className="f-11 text-muted mb-2 H">
        <i>{getChartTitle(dataField)} - Last 24 Hours</i>
      </div>
    </>
  );
};

export default AnalyticsChart;
